import { useState } from 'react'
import { X, LayoutTemplate, Trash2 } from 'lucide-react'
import { useTemplateStore } from '@/stores/templateStore'
import type { TaskTemplate, TaskPriority, RepeatPattern } from '@/lib/types'

interface TemplateData {
  title: string
  categoryId: number | null
  priority: TaskPriority
  subtasks: { title: string; sortOrder: number }[]
  repeat: RepeatPattern
  memo?: string
}

interface TemplatePickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (data: TemplateData) => void
}

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  none: '없음',
  low: '낮음',
  medium: '중간',
  high: '높음',
}

export function TemplatePickerModal({ isOpen, onClose, onSelect }: TemplatePickerModalProps) {
  const templates = useTemplateStore((s) => s.templates)
  const getBuiltInTemplates = useTemplateStore((s) => s.getBuiltInTemplates)
  const deleteTemplate = useTemplateStore((s) => s.deleteTemplate)
  const [activeTab, setActiveTab] = useState<'builtin' | 'custom'>('builtin')

  if (!isOpen) return null

  const builtInTemplates = getBuiltInTemplates()
  const customTemplates = templates.filter((t) => !t.isBuiltIn)

  const handleSelect = (template: Omit<TaskTemplate, 'id'> | TaskTemplate) => {
    onSelect({
      title: template.name,
      categoryId: template.categoryId,
      priority: template.priority,
      subtasks: template.subtasks,
      repeat: template.repeat,
      memo: template.memo,
    })
    onClose()
  }

  const renderTemplate = (template: Omit<TaskTemplate, 'id'> | TaskTemplate, index: number) => {
    const hasId = 'id' in template && template.id != null
    return (
      <button
        key={hasId ? template.id : `builtin-${index}`}
        type="button"
        onClick={() => handleSelect(template)}
        className="w-full text-left p-3 rounded-xl border border-strong hover:border-[var(--hover-border)] hover-surface transition-colors group"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-heading">{template.name}</h4>
            {template.description && (
              <p className="text-xs text-caption mt-0.5">{template.description}</p>
            )}
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-meta px-1.5 py-0.5 rounded bg-surface-tertiary text-caption">
                하위 작업 {template.subtasks.length}개
              </span>
              {template.priority !== 'none' && (
                <span className="text-meta px-1.5 py-0.5 rounded bg-surface-tertiary text-caption">
                  {PRIORITY_LABELS[template.priority]}
                </span>
              )}
            </div>
          </div>
          {hasId && !template.isBuiltIn && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                deleteTemplate((template as TaskTemplate).id!)
              }}
              className="p-1 rounded opacity-0 group-hover:opacity-100 text-disabled hover:text-red-500 transition-all"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </button>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md max-h-[80dvh] pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 bg-surface-primary rounded-t-2xl sm:rounded-2xl border border-base shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-base">
          <div className="flex items-center gap-2">
            <LayoutTemplate className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-heading">템플릿 선택</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-disabled hover:text-[var(--text-secondary)] hover-surface transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-base">
          <button
            type="button"
            onClick={() => setActiveTab('builtin')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'builtin'
                ? 'text-accent-primary border-b-2 border-primary-500'
                : 'text-caption hover:text-[var(--text-secondary)]'
            }`}
          >
            기본 템플릿
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('custom')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'custom'
                ? 'text-accent-primary border-b-2 border-primary-500'
                : 'text-caption hover:text-[var(--text-secondary)]'
            }`}
          >
            내 템플릿 ({customTemplates.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {activeTab === 'builtin' ? (
            builtInTemplates.map((t, i) => renderTemplate(t, i))
          ) : customTemplates.length > 0 ? (
            customTemplates.map((t, i) => renderTemplate(t, i))
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-disabled">
              <LayoutTemplate className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm">저장된 템플릿이 없습니다</p>
              <p className="text-xs mt-1">작업 상세에서 "템플릿으로 저장"을 사용하세요</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
